// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Cost Dashboard
// Spend tracking, burn rates, contract ceiling projections,
// force account vs. contractor split, all-vendor view
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useCallback, useState } from 'react';
import {
  SimpleGrid, Paper, Text, Title, Group, Badge, Stack, Table, Loader, Center,
} from '../../components/gab/GabComponents';
import {
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useAllTickets } from '../../hooks/useAllTickets';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { fetchRecords, queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { formatCurrency } from '../../lib/formatUtils';

// ── Sortable header helper ─────────────────────────────────
function SortTh({ label, field, sortField, sortDir, onSort, ta }: {
  label: string; field: string; sortField: string; sortDir: 'asc' | 'desc';
  onSort: (f: string) => void; ta?: 'right' | 'left';
}) {
  return (
    <Table.Th ta={ta} style={{
      cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      color: sortField === field ? '#4B3FFF' : '#64748b',
      position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc',
    }} onClick={() => onSort(field)}>
      {label}
      {sortField === field
        ? (sortDir === 'desc' ? ' \u2193' : ' \u2191')
        : <span style={{ opacity: 0.25 }}> \u2191</span>}
    </Table.Th>
  );
}

// ── Compact currency formatter (for chart labels) ─────────
const fmtK = (n: number) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n.toFixed(0)}`;

// ── Contractor bar colors ──────────────────────────────────
const CONTRACTOR_COLORS = ['#4B3FFF', '#0284c7', '#16a34a', '#d97706', '#B12525', '#7c3aed', '#db2777', '#059669'];

// Force account types
interface FALabor { employeeName: string; department: string; totalCost: number; }
interface FAEquipment { fleetNumber: string; equipmentType: string; totalCost: number; }

export default function CostDashboard() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    zones, contractors, trucks, disposalSites,
    loadingZones, loadingContractors, loadingSites,
    loadZones, loadContractors, loadTrucks, loadDisposalSites,
  } = useDebrisStore();

  const { invoices, loadInvoices, loadingInvoices } = useDebrisOpsStore();

  // Shared cached hook — all tickets for active incident (full meta including TDMS fields)
  const { tickets: allTickets, loading: loadingTickets } = useAllTickets();
  const { reconciliations, loadReconciliations } = useTampaDebrisStore();

  // Force account data
  const [faLaborCost, setFaLaborCost] = useState(0);
  const [faEquipmentCost, setFaEquipmentCost] = useState(0);
  const [faLaborCount, setFaLaborCount] = useState(0);
  const [faEquipmentCount, setFaEquipmentCount] = useState(0);
  const [loadingFA, setLoadingFA] = useState(false);

  useEffect(() => {
    loadZones(); loadContractors(); loadTrucks(); loadDisposalSites();
    loadInvoices(); loadReconciliations();
  }, [loadZones, loadContractors, loadTrucks, loadDisposalSites, loadInvoices, loadReconciliations]);

  // Load force account totals
  useEffect(() => {
    if (!incidentId) return;
    setLoadingFA(true);
    const FL = FIELDS.debrisForceAccountLabor;
    const FE = FIELDS.debrisForceAccountEquipment;

    const incFilter = (field: string) => [{ field, operator: 'eq' as const, value: incidentId }];
    Promise.all([
      queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountLabor, {
        limit: 500, filters: incFilter(FL.incidentId),
      }).then(res => {
        const records = res.records.filter(r => String(r[FL.incidentId] ?? '') === incidentId);
        setFaLaborCount(records.length);
        setFaLaborCost(records.reduce((s, r) => s + (Number(r[FL.totalCost]) || 0), 0));
      }).catch(() => {}),
      queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountEquipment, {
        limit: 500, filters: incFilter(FE.incidentId),
      }).then(res => {
        const records = res.records.filter(r => String(r[FE.incidentId] ?? '') === incidentId);
        setFaEquipmentCount(records.length);
        setFaEquipmentCost(records.reduce((s, r) => s + (Number(r[FE.totalCost]) || 0), 0));
      }).catch(() => {}),
    ]).finally(() => setLoadingFA(false));
  }, [incidentId]);

  const iZones = incidentId ? zones : [];
  const iContractors = incidentId ? contractors : [];
  const iTrucks = incidentId ? trucks : [];
  const iTickets = incidentId ? allTickets : [];
  const iInvoices = incidentId ? invoices : [];
  const iReconciliations = incidentId ? reconciliations : [];

  // ── Compute CY ────────────────────────────────────────────
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = iTrucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [iTrucks]);

  // ── Summary cards ─────────────────────────────────────────
  const totalInvoiced = useMemo(() =>
    iInvoices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0), [iInvoices]);

  const totalApproved = useMemo(() =>
    iReconciliations.reduce((s, r) => s + (r.approvedAmount || 0), 0), [iReconciliations]);

  const pendingRecon = useMemo(() =>
    iInvoices.filter(i => i.status === 'submitted' || i.status === 'draft')
      .reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0), [iInvoices]);

  // ── Payment tracking from invoice notes ─────────────────
  const PAYMENTS_RE = /\|\|\|PAYMENTS\|\|\|([\s\S]*?)\|\|\|ENDPAYMENTS\|\|\|/;

  const paymentSummary = useMemo(() => {
    let totalPaid = 0;

    iInvoices.forEach(inv => {
      const rawNotes = inv.notes ?? '';
      // Parse payments
      const pm = rawNotes.match(PAYMENTS_RE);
      if (pm) {
        try {
          const payments: { amount: number }[] = JSON.parse(pm[1]);
          totalPaid += payments.reduce((s, p) => s + (p.amount || 0), 0);
        } catch { /* ignore */ }
      }
    });

    const outstanding = totalApproved - totalPaid;
    return { totalPaid, outstanding };
  }, [iInvoices, totalApproved]);

  // ── Force Account vs. Contractor totals ───────────────────
  const faTotalCost = faLaborCost + faEquipmentCost;
  const contractorTotalCost = totalInvoiced;
  const totalEventCost = contractorTotalCost + faTotalCost;

  // ── All-Vendor Spend (all contractors, not just primes) ────
  const allVendorSpend = useMemo(() => {
    const META_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
    const contMap = Object.fromEntries(iContractors.map(c => [String(c.id), c]));
    const byVendor: Record<string, { name: string; category: string; department: string; spend: number }> = {};
    iInvoices.forEach(inv => {
      const cont = contMap[inv.contractorId];
      const name = cont?.companyName || `Contractor ${inv.contractorId}`;
      // Determine Prime vs Sub from TRMETA primeContractorId in notes
      let isSub = false;
      if (cont?.notes) {
        const m = META_RE.exec(cont.notes);
        if (m) { try { isSub = !!JSON.parse(m[1]).primeContractorId; } catch {} }
      }
      const category = isSub ? 'Sub' : 'Prime';
      if (!byVendor[name]) byVendor[name] = { name, category, department: '', spend: 0 };
      byVendor[name].spend += parseFloat(inv.totalAmount) || 0;
    });
    return Object.values(byVendor).sort((a, b) => b.spend - a.spend);
  }, [iInvoices, iContractors]);

  // ── By Contractor ─────────────────────────────────────────
  const contractorSpend = useMemo(() => {
    const contMap = Object.fromEntries(iContractors.map(c => [String(c.id), c]));
    const byContractor: Record<string, { name: string; approved: number; ceiling: number; tickets: number; cy: number }> = {};
    iInvoices.forEach(inv => {
      const cont = contMap[inv.contractorId];
      const name = cont?.companyName || `Contractor ${inv.contractorId}`;
      if (!byContractor[name]) byContractor[name] = { name, approved: 0, ceiling: 0, tickets: 0, cy: 0 };
      byContractor[name].approved += parseFloat(inv.totalAmount) || 0;
    });
    iTickets.forEach(t => {
      const cont = contMap[t.contractorId];
      const name = cont?.companyName || `Contractor ${t.contractorId || 'Unknown'}`;
      if (!byContractor[name]) byContractor[name] = { name, approved: 0, ceiling: 0, tickets: 0, cy: 0 };
      byContractor[name].tickets++;
      byContractor[name].cy += getTicketCY(t);
    });
    iContractors.forEach(c => {
      if (byContractor[c.companyName]) {
        byContractor[c.companyName].ceiling = parseFloat(c.contractAmount || '0') || 0;
      }
    });
    return Object.values(byContractor).sort((a, b) => b.approved - a.approved);
  }, [iInvoices, iTickets, iContractors, getTicketCY]);

  const maxContractorVal = useMemo(() => {
    let m = 0;
    contractorSpend.forEach(c => { m = Math.max(m, c.approved, c.ceiling); });
    return m || 1;
  }, [contractorSpend]);

  // ── By Debris Type (donut) ────────────────────────────────
  const cyByType = useMemo(() => {
    const map: Record<string, number> = {};
    iTickets.forEach(t => {
      if (t.debrisType) map[t.debrisType] = (map[t.debrisType] || 0) + getTicketCY(t);
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [iTickets, getTicketCY]);

  const totalCY = useMemo(() => iTickets.reduce((s, t) => s + getTicketCY(t), 0), [iTickets, getTicketCY]);

  // ── By Zone ───────────────────────────────────────────────
  const zoneData = useMemo(() => {
    const map: Record<string, { zoneName: string; ercDistrict: string; totalCY: number; totalCost: number; tickets: number }> = {};
    iTickets.forEach(t => {
      if (!t.zoneId) return;
      if (!map[t.zoneId]) {
        const zone = iZones.find(z => String(z.id) === t.zoneId);
        map[t.zoneId] = {
          zoneName: zone?.zoneName || `Zone ${t.zoneId}`,
          ercDistrict: '',
          totalCY: 0, totalCost: 0, tickets: 0,
        };
      }
      const cy = getTicketCY(t);
      map[t.zoneId].totalCY += cy;
      map[t.zoneId].totalCost += parseFloat(t.extendedCost) || 0;
      map[t.zoneId].tickets++;
    });
    return Object.entries(map).map(([id, d]) => ({ id, ...d }));
  }, [iTickets, iZones, getTicketCY]);

  const [zoneSortField, setZoneSortField] = useState('totalCY');
  const [zoneSortDir, setZoneSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleZoneSort = (field: string) => {
    if (zoneSortField === field) setZoneSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setZoneSortField(field); setZoneSortDir('desc'); }
  };
  const sortedZones = [...zoneData].sort((a, b) => {
    if (!zoneSortField) return 0;
    const va = (a as any)[zoneSortField] ?? '';
    const vb = (b as any)[zoneSortField] ?? '';
    const n = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return zoneSortDir === 'asc' ? n : -n;
  });

  // All-vendor sort
  const [vendorSortField, setVendorSortField] = useState('spend');
  const [vendorSortDir, setVendorSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleVendorSort = (field: string) => {
    if (vendorSortField === field) setVendorSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setVendorSortField(field); setVendorSortDir('desc'); }
  };
  const sortedVendors = [...allVendorSpend].sort((a, b) => {
    if (!vendorSortField) return 0;
    const va = (a as any)[vendorSortField] ?? '';
    const vb = (b as any)[vendorSortField] ?? '';
    const n = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return vendorSortDir === 'asc' ? n : -n;
  });
  const totalAllVendorSpend = allVendorSpend.reduce((s, v) => s + v.spend, 0);

  // ── Burn Rate ─────────────────────────────────────────────
  const burnRate = useMemo(() => {
    const times = iTickets.map(t => t.pickupTime ? new Date(t.pickupTime).getTime() : 0).filter(t => t > 0);
    if (times.length === 0) return { dailyAvg: 0, daysSinceFirst: 0 };
    const first = Math.min(...times);
    const daysSinceFirst = Math.max(1, Math.ceil((Date.now() - first) / 86400000));
    const dailyAvg = totalApproved > 0 ? totalApproved / daysSinceFirst : totalInvoiced / daysSinceFirst;
    return { dailyAvg, daysSinceFirst };
  }, [iTickets, totalApproved, totalInvoiced]);

  const isLoading = loadingZones || loadingContractors || loadingSites || loadingTickets || loadingInvoices;

  if (isLoading && iTickets.length === 0 && iInvoices.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  // Donut chart helpers
  const donutSize = 200;
  const donutRadius = 75;
  const donutStroke = 30;

  return (
    <Stack gap="lg">
      <div>
        <Title order={3} c="black">Cost Dashboard</Title>
        <Text size="sm" c="dimmed">
          Paid costs, substantiation status, and contract ceiling projections
        </Text>
      </div>

      {/* Summary cards */}
      <SimpleGrid cols={{ base: 2, sm: 3 }}>
        <CostCard label="Total Invoiced" value={formatCurrency(totalInvoiced)}
          sub={`${iInvoices.length} invoices`} color="#0284c7" />
        <CostCard label="Total Approved" value={formatCurrency(totalApproved)}
          sub={iReconciliations.length > 0 ? `${iReconciliations.filter(r => r.status === 'approved').length} reconciliations` : 'No reconciliations yet'} color="#16a34a" />
        <CostCard label="Pending Reconciliation" value={formatCurrency(pendingRecon)}
          sub={`${iInvoices.filter(i => i.status === 'submitted' || i.status === 'draft').length} invoices`} color="#B12525" />
      </SimpleGrid>

      {/* Payment Summary */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Payment Summary</Text>
        <SimpleGrid cols={{ base: 2, sm: 3 }}>
          <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Approved</Text>
            <Text fz={22} fw={800} c="#16a34a">{formatCurrency(totalApproved)}</Text>
          </Paper>
          <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #03AC9E' }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Paid</Text>
            <Text fz={22} fw={800} c="#03AC9E">{formatCurrency(paymentSummary.totalPaid)}</Text>
          </Paper>
          <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #B12525' }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Outstanding</Text>
            <Text fz={22} fw={800} c="#B12525">{formatCurrency(paymentSummary.outstanding)}</Text>
          </Paper>
        </SimpleGrid>
      </Paper>

      {/* Cost Breakdown by Source — Force Account vs. Contractor */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Cost Breakdown by Source (FEMA Category)</Text>
        {loadingFA ? (
          <Center h={80}><Loader size="sm" /></Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #0284c7' }}>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Contractor Costs</Text>
              <Text fz={28} fw={800} c="#0284c7">{formatCurrency(contractorTotalCost)}</Text>
              <Text size="xs" c="dimmed">{iInvoices.length} invoices from {contractorSpend.length} contractors</Text>
              {totalEventCost > 0 && (
                <Badge size="xs" mt={4} color="#0284c7" variant="light">
                  {((contractorTotalCost / totalEventCost) * 100).toFixed(1)}% of total
                </Badge>
              )}
            </Paper>
            <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Force Account Costs</Text>
              <Text fz={28} fw={800} c="#d97706">{faTotalCost > 0 ? formatCurrency(faTotalCost) : '--'}</Text>
              <Stack gap={2} align="center">
                <Text size="xs" c="dimmed">Labor: {formatCurrency(faLaborCost)} ({faLaborCount} records)</Text>
                <Text size="xs" c="dimmed">Equipment: {formatCurrency(faEquipmentCost)} ({faEquipmentCount} records)</Text>
              </Stack>
              {totalEventCost > 0 && faTotalCost > 0 && (
                <Badge size="xs" mt={4} color="#d97706" variant="light">
                  {((faTotalCost / totalEventCost) * 100).toFixed(1)}% of total
                </Badge>
              )}
            </Paper>
            <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Event Cost</Text>
              <Text fz={28} fw={800} c="#16a34a">{formatCurrency(totalEventCost)}</Text>
              <Text size="xs" c="dimmed">Contractors + Force Account</Text>
              <Text size="xs" c="dimmed" mt={4}>{totalCY.toLocaleString()} CY removed</Text>
            </Paper>
          </SimpleGrid>
        )}
      </Paper>

      {/* By Contractor + By Debris Type */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* By Contractor -- Horizontal bar chart */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="md">Spend by Contractor</Text>
          {contractorSpend.length === 0 ? (
            <Text size="sm" c="dimmed">No invoice data yet</Text>
          ) : (
            <Stack gap="sm">
              <svg width="100%" viewBox={`0 0 500 ${contractorSpend.length * 50 + 10}`} style={{ overflow: 'visible' }}>
                {contractorSpend.map((c, i) => {
                  const barW = maxContractorVal > 0 ? (c.approved / maxContractorVal) * 400 : 0;
                  const ceilW = c.ceiling > 0 && maxContractorVal > 0 ? (c.ceiling / maxContractorVal) * 400 : 0;
                  const color = CONTRACTOR_COLORS[i % CONTRACTOR_COLORS.length];
                  return (
                    <g key={c.name} transform={`translate(0, ${i * 50})`}>
                      <text x="0" y="14" fontSize="12" fontWeight="600" fill="#1e293b">
                        {c.name.length > 30 ? c.name.substring(0, 27) + '...' : c.name}
                      </text>
                      {ceilW > 0 && (
                        <rect x="0" y="20" width={ceilW} height="20" rx="4"
                          fill={color} opacity="0.15" />
                      )}
                      <rect x="0" y="20" width={Math.max(barW, 2)} height="20" rx="4"
                        fill={color} opacity="0.85" />
                      <text x={Math.max(barW, 2) + 8} y="35" fontSize="11" fill="#64748b">
                        {fmtK(c.approved)}{c.ceiling > 0 ? ` / ${fmtK(c.ceiling)}` : ''}
                      </text>
                    </g>
                  );
                })}
              </svg>
              {contractorSpend.filter(c => c.ceiling > 0 && c.approved / c.ceiling > 0.8).map(c => {
                const pct = Math.round((c.approved / c.ceiling) * 100);
                return (
                  <Group key={c.name} gap="xs" style={{ padding: '4px 8px', background: pct >= 95 ? '#FEE2E2' : '#FEF3C7', borderRadius: 6 }}>
                    <IconAlertTriangle size={14} color={pct >= 95 ? '#B12525' : '#d97706'} />
                    <Text size="xs" fw={500} c={pct >= 95 ? '#B12525' : '#92400e'}>
                      {c.name} at {pct}% of ceiling ({fmtK(c.approved)} / {fmtK(c.ceiling)})
                    </Text>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* By Debris Type -- Donut chart */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="md">Volume by Debris Type</Text>
          {cyByType.length === 0 ? (
            <Text size="sm" c="dimmed">No ticket data yet</Text>
          ) : (
            <Stack gap="md" align="center">
              <svg width={donutSize} height={donutSize} viewBox={`0 0 ${donutSize} ${donutSize}`}>
                {(() => {
                  let cumAngle = -90;
                  const cx = donutSize / 2;
                  const cy2 = donutSize / 2;
                  return cyByType.map(([type, val]) => {
                    const pct = totalCY > 0 ? val / totalCY : 0;
                    const angle = pct * 360;
                    const startRad = (cumAngle * Math.PI) / 180;
                    const endRad = ((cumAngle + angle) * Math.PI) / 180;
                    cumAngle += angle;
                    const largeArc = angle > 180 ? 1 : 0;
                    const x1 = cx + donutRadius * Math.cos(startRad);
                    const y1 = cy2 + donutRadius * Math.sin(startRad);
                    const x2 = cx + donutRadius * Math.cos(endRad);
                    const y2 = cy2 + donutRadius * Math.sin(endRad);
                    const color = DEBRIS_TYPE_COLORS[type as DebrisType] ?? '#6b7280';
                    if (pct < 0.001) return null;
                    return (
                      <path
                        key={type}
                        d={`M ${x1} ${y1} A ${donutRadius} ${donutRadius} 0 ${largeArc} 1 ${x2} ${y2}`}
                        fill="none"
                        stroke={color}
                        strokeWidth={donutStroke}
                        strokeLinecap="butt"
                      >
                        <title>{DEBRIS_TYPE_LABELS[type as DebrisType] ?? type}: {val.toLocaleString()} CY ({(pct * 100).toFixed(1)}%)</title>
                      </path>
                    );
                  });
                })()}
                <text x={donutSize / 2} y={donutSize / 2 - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill="#1e293b">
                  {totalCY.toLocaleString()}
                </text>
                <text x={donutSize / 2} y={donutSize / 2 + 12} textAnchor="middle" fontSize="11" fill="#94a3b8">
                  Total CY
                </text>
              </svg>
              <Stack gap={4} style={{ width: '100%' }}>
                {cyByType.map(([type, val]) => {
                  const pct = totalCY > 0 ? (val / totalCY) * 100 : 0;
                  const color = DEBRIS_TYPE_COLORS[type as DebrisType] ?? '#6b7280';
                  return (
                    <Group key={type} justify="space-between">
                      <Group gap="xs">
                        <div style={{ width: 10, height: 10, borderRadius: 4, backgroundColor: color }} />
                        <Text size="sm" fw={500}>{DEBRIS_TYPE_LABELS[type as DebrisType] ?? type}</Text>
                      </Group>
                      <Text size="sm" c="dimmed">{val.toLocaleString()} CY ({pct.toFixed(1)}%)</Text>
                    </Group>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* All-Vendor Spend Table */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={700}>All-Vendor Spend</Text>
          <Text size="sm" fw={600} c="var(--color-primary-700)">{formatCurrency(totalAllVendorSpend)} total across {allVendorSpend.length} vendors</Text>
        </Group>
        {allVendorSpend.length === 0 ? (
          <Text size="sm" c="dimmed">No vendor spend data yet</Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 500px)' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <SortTh label="Vendor" field="name" sortField={vendorSortField} sortDir={vendorSortDir} onSort={toggleVendorSort} />
                  <SortTh label="Category" field="category" sortField={vendorSortField} sortDir={vendorSortDir} onSort={toggleVendorSort} />
                  <SortTh label="Spend" field="spend" sortField={vendorSortField} sortDir={vendorSortDir} onSort={toggleVendorSort} ta="right" />
                  <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>% of Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedVendors.map(v => (
                  <Table.Tr key={v.name}>
                    <Table.Td><Text size="sm" fw={500}>{v.name}</Text></Table.Td>
                    <Table.Td><Badge size="xs" variant="light">{v.category}</Badge></Table.Td>
                    <Table.Td ta="right"><Text size="sm" ff="monospace" fw={600}>{formatCurrency(v.spend)}</Text></Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" c="dimmed">{totalAllVendorSpend > 0 ? ((v.spend / totalAllVendorSpend) * 100).toFixed(1) : 0}%</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Paper>

      {/* By Zone Table */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="sm">Cost by Zone</Text>
        {zoneData.length === 0 ? (
          <Text size="sm" c="dimmed">No ticket data by zone yet</Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 500px)' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <SortTh label="Zone" field="zoneName" sortField={zoneSortField} sortDir={zoneSortDir} onSort={toggleZoneSort} />
                  <SortTh label="Total CY" field="totalCY" sortField={zoneSortField} sortDir={zoneSortDir} onSort={toggleZoneSort} ta="right" />
                  <SortTh label="Total Cost" field="totalCost" sortField={zoneSortField} sortDir={zoneSortDir} onSort={toggleZoneSort} ta="right" />
                  <SortTh label="# Tickets" field="tickets" sortField={zoneSortField} sortDir={zoneSortDir} onSort={toggleZoneSort} ta="right" />
                  <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>Avg Cost/CY</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedZones.map(z => (
                  <Table.Tr key={z.id}>
                    <Table.Td><Text size="sm" fw={500}>{z.zoneName}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{z.totalCY.toLocaleString()}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{formatCurrency(z.totalCost)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{z.tickets}</Text></Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{z.totalCY > 0 ? formatCurrency(z.totalCost / z.totalCY) : '--'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Paper>

      {/* Burn Rate */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Burn Rate</Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #0284c7' }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Daily Average Spend</Text>
            <Text fz={28} fw={700} c="#0284c7">
              {burnRate.dailyAvg > 0 ? fmtK(burnRate.dailyAvg) : '--'}
            </Text>
            <Text size="xs" c="dimmed">
              {burnRate.daysSinceFirst > 0 ? `Over ${burnRate.daysSinceFirst} days` : 'No ticket data'}
            </Text>
          </Paper>
          <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Est. Days to Ceiling</Text>
            <Stack gap={2} align="center">
              {contractorSpend.filter(c => c.ceiling > 0 && burnRate.dailyAvg > 0).length === 0 ? (
                <Text fz={28} fw={700} c="#d97706">--</Text>
              ) : (
                contractorSpend.filter(c => c.ceiling > 0).map(c => {
                  const remaining = c.ceiling - c.approved;
                  const daysLeft = burnRate.dailyAvg > 0 ? Math.ceil(remaining / burnRate.dailyAvg) : Infinity;
                  return (
                    <Group key={c.name} justify="space-between" style={{ width: '100%' }}>
                      <Text size="xs" truncate style={{ flex: 1 }}>{c.name}</Text>
                      <Text size="sm" fw={700} c={daysLeft < 30 ? '#B12525' : '#d97706'}>
                        {daysLeft === Infinity ? '\u221E' : `${daysLeft}d`}
                      </Text>
                    </Group>
                  );
                })
              )}
            </Stack>
          </Paper>
          <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Event Spend</Text>
            <Text fz={28} fw={700} c="#16a34a">{formatCurrency(totalEventCost)}</Text>
            <Text size="xs" c="dimmed">{totalCY.toLocaleString()} CY removed</Text>
          </Paper>
        </SimpleGrid>
      </Paper>
    </Stack>
  );
}

// ── Cost Card Component ────────────────────────────────────
function CostCard({ label, value, sub, color }: {
  label: string; value: string; sub: string; color: string;
}) {
  return (
    <Paper p="sm" radius="md" withBorder ta="center"
      style={{
        borderRadius: 12, borderTop: `3px solid ${color}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        transition: 'transform 150ms, box-shadow 150ms',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}>
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">{label}</Text>
      <Text fz={32} fw={800} style={{ color }}>{value}</Text>
      <Text size="xs" c="#94a3b8">{sub}</Text>
    </Paper>
  );
}
